import { useEffect, useState } from "react"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from "recharts"

import {
  fetchConferenceStats,
  fetchConferenceDaily,
  fetchConferenceMonthly
} from "../services/registrations.service"

import {
  getMembershipCount,
  getMembershipDaily,
  getMembershipMonthly,
  getSiteVisitsCount,
  getSiteVisitsDaily,
  getSiteVisitsMonthly,
  getTopPages,
  getVisitsByCountry,
  getVisitsByDevice
} from "../services/analytics.service"

export default function DashboardOverview() {
  const [conferenceCount, setConferenceCount] = useState(0)
  const [membershipCount, setMembershipCount] = useState(0)
  const [siteVisitsCount, setSiteVisitsCount] = useState(0)
  const [topPages, setTopPages] = useState([])
  const [visitsByCountry, setVisitsByCountry] = useState([])
  const [visitsByDevice, setVisitsByDevice] = useState([])

  const [confDaily, setConfDaily] = useState([])
  const [confMonthly, setConfMonthly] = useState([])
  const [memberDaily, setMemberDaily] = useState([])
  const [memberMonthly, setMemberMonthly] = useState([])
  const [visitsDaily, setVisitsDaily] = useState([])
  const [visitsMonthly, setVisitsMonthly] = useState([])

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    const [
      confCount,
      memberCount,
      visitsCount,
      confDay,
      confMonth,
      memberDay,
      memberMonth,
      visitsDay,
      visitsMonth,
      pages,
      countries,
      devices
    ] = await Promise.all([
      fetchConferenceStats(),
      getMembershipCount(),
      getSiteVisitsCount(),
      fetchConferenceDaily(),
      fetchConferenceMonthly(),
      getMembershipDaily(),
      getMembershipMonthly(),
      getSiteVisitsDaily(),
      getSiteVisitsMonthly(),
      getTopPages(),
      getVisitsByCountry(),
      getVisitsByDevice()
    ])

    setConferenceCount(confCount)
    setMembershipCount(memberCount)
    setSiteVisitsCount(visitsCount)
    setConfDaily(confDay || [])
    setConfMonthly(confMonth || [])
    setMemberDaily(memberDay || [])
    setMemberMonthly(memberMonth || [])
    setVisitsDaily(visitsDay || [])
    setVisitsMonthly(visitsMonth || [])
    setTopPages(pages || [])
    setVisitsByCountry(countries || [])
    setVisitsByDevice(devices || [])
  }

  return (
    <div className="space-y-8">
      {/* Welcome Info Notice */}
      <div className="bg-gradient-to-r from-amber-50 to-blue-50 border border-amber-200 rounded-lg p-5 flex items-start gap-4">
        <div className="flex-shrink-0 mt-0.5">
          <svg className="w-6 h-6 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/>
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-slate-900 mb-2">
            Welcome to Africamed Business Forum Admin Dashboard
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            This is your central hub for managing the conference. Track registration numbers and trends in real-time. 
            Use the sidebar to navigate to <span className="font-semibold">Conference Registrations</span> to approve/reject participants, 
            <span className="font-semibold"> Club Memberships</span> to manage applications, 
            <span className="font-semibold"> Speakers</span> to add and manage speaker profiles, and 
            <span className="font-semibold"> Payment Validations</span> to verify transactions.
          </p>
        </div>
      </div>

      {/* Top statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Conference Registrations" value={conferenceCount} />
        <StatCard title="Club Membership Applications" value={membershipCount} />
        <StatCard title="Total Site Visits" value={siteVisitsCount} />
        <StatCard title="Pending Payments" value="—" muted />
      </div>

      {/* Conference charts */}
      <Section title="Conference Registrations Trend">
        <ChartBlock title="Daily Growth">
          <LineGraph data={confDaily} />
        </ChartBlock>

        <ChartBlock title="Monthly Growth">
          <LineGraph data={confMonthly} />
        </ChartBlock>
      </Section>

      {/* Membership charts */}
      <Section title="Club Membership Applications Trend">
        <ChartBlock title="Daily Growth">
          <LineGraph data={memberDaily} />
        </ChartBlock>

        <ChartBlock title="Monthly Growth">
          <LineGraph data={memberMonthly} />
        </ChartBlock>
      </Section>

      {/* Site Analytics Section */}
      <Section title="Website Analytics">
        <ChartBlock title="Daily Visits">
          <LineGraph data={visitsDaily} />
        </ChartBlock>

        <ChartBlock title="Monthly Visits">
          <LineGraph data={visitsMonthly} />
        </ChartBlock>
      </Section>

      {/* Top Pages */}
      {topPages.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">
            Top Visited Pages (Last 30 Days)
          </h2>
          <div className="space-y-2">
            {topPages.map((page, idx) => (
              <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                <span className="text-sm font-medium text-slate-700">{page.page}</span>
                <span className="text-sm font-bold text-amber-600">{page.visits} visits</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Visitors by Country and Device */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Visitors by Country */}
        {visitsByCountry.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Top Countries (Last 30 Days)
            </h2>
            <div className="space-y-2">
              {visitsByCountry.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{getCountryFlag(item.country)}</span>
                    <span className="text-sm font-medium text-slate-700">{item.country}</span>
                  </div>
                  <span className="text-sm font-bold text-blue-600">{item.visits} visits</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Visitors by Device */}
        {visitsByDevice.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Visits by Device Type (Last 30 Days)
            </h2>
            <div className="space-y-2">
              {visitsByDevice.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{getDeviceIcon(item.device)}</span>
                    <span className="text-sm font-medium text-slate-700">{item.device}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-green-600">{item.visits} visits</span>
                    <span className="text-xs text-slate-500">
                      {((item.visits / siteVisitsCount) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

    </div>
  )
}

/* ---------------- Components ---------------- */

function StatCard({ title, value, muted }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <p className="text-sm text-gray-500">{title}</p>
      <p
        className={`text-3xl font-bold mt-2 ${
          muted ? "text-gray-400" : "text-slate-900"
        }`}
      >
        {value ?? "—"}
      </p>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-slate-900 mb-6">
        {title}
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {children}
      </div>
    </div>
  )
}

function ChartBlock({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-medium text-black mb-3">
        {title}
      </h3>
      {children}
    </div>
  )
}

function LineGraph({ data }) {
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#f59e0b"
            strokeWidth={3}
            dot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

/* Helper functions for icons and flags */
function getCountryFlag(country) {
  const flagMap = {
    'Morocco': '🇲🇦',
    'United States': '🇺🇸',
    'France': '🇫🇷',
    'United Kingdom': '🇬🇧',
    'Germany': '🇩🇪',
    'Spain': '🇪🇸',
    'Italy': '🇮🇹',
    'Canada': '🇨🇦',
    'Nigeria': '🇳🇬',
    'South Africa': '🇿🇦',
    'Egypt': '🇪🇬',
    'Kenya': '🇰🇪',
    'Ghana': '🇬🇭',
    'Algeria': '🇩🇿',
    'Tunisia': '🇹🇳',
    'Senegal': '🇸🇳',
    'Unknown': '🌍'
  };
  return flagMap[country] || '🌍';
}

function getDeviceIcon(device) {
  const iconMap = {
    'Mobile': '📱',
    'Desktop': '💻',
    'Tablet': '📲',
    'Unknown': '🖥️'
  };
  return iconMap[device] || '🖥️';
}
